import React, { useState } from 'react';
import { supabase } from '../admin-app/supabase';
import { normalizeEmail } from '../utils/string';
import { Modal } from './Modal';
import { getErrorMessage } from '../utils/errorMessage';
import { getAllSchools } from '../config/schools';

interface CsvImportProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (results: ImportResults) => void;
}

interface ImportResults {
  success: number;
  errors: string[];
  newParents: number;
  newStudents: number;
}

interface CsvRow {
  guardian1_full_name: string;
  guardian1_email: string;
  guardian1_phone_number: string;
  guardian2_full_name: string;
  guardian2_email: string;
  guardian2_phone_number: string;
  student_full_name: string;
  Grade: string;
  school: string;
  dob: string;
}

export function CsvImport({ isOpen, onClose, onSuccess }: CsvImportProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState<CsvRow[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      parsePreview(selectedFile);
    }
  };

  const parsePreview = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        setError('CSV file must have at least a header row and one data row');
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim());
      const requiredHeaders = ['guardian1_full_name', 'guardian1_email', 'guardian1_phone_number', 'guardian2_full_name', 'guardian2_email', 'guardian2_phone_number', 'student_full_name', 'Grade', 'school', 'dob'];
      
      const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
      if (missingHeaders.length > 0) {
        setError(`Missing required columns: ${missingHeaders.join(', ')}`);
        return;
      }

      const previewData = lines.slice(1, 6).map(line => {
        const values = line.split(',').map(v => v.trim());
        const row: Record<string, string> = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        
        // Apply school name normalization for preview
        const rawRow = row as unknown as CsvRow;
        if (rawRow.school) {
          const { match } = findMatchingSchool(rawRow.school);
          if (match) {
            rawRow.school = match;
          }
        }
        
        return rawRow;
      });

      setPreview(previewData);
      setShowPreview(true);
    };
    reader.readAsText(file);
  };

  const validateRow = (row: CsvRow, rowIndex: number): { errors: string[]; normalizedRow: CsvRow } => {
    const errors: string[] = [];
    const normalizedRow = { ...row };
    
    // Guardian 1 validation
    if (!row.guardian1_full_name?.trim()) {
      errors.push(`Row ${rowIndex + 1}: Guardian 1 full name is required`);
    }
    
    if (!row.guardian1_email?.trim()) {
      errors.push(`Row ${rowIndex + 1}: Guardian 1 email is required`);
    } else if (!row.guardian1_email.includes('@')) {
      errors.push(`Row ${rowIndex + 1}: Invalid Guardian 1 email format`);
    }
    
    // Guardian 2 validation (optional, but if provided must be valid)
    if (row.guardian2_full_name?.trim() && !row.guardian2_email?.trim()) {
      errors.push(`Row ${rowIndex + 1}: Guardian 2 email is required when Guardian 2 name is provided`);
    }
    
    if (row.guardian2_email?.trim() && !row.guardian2_email.includes('@')) {
      errors.push(`Row ${rowIndex + 1}: Invalid Guardian 2 email format`);
    }
    
    if (!row.student_full_name?.trim()) {
      errors.push(`Row ${rowIndex + 1}: Student full name is required`);
    }
    
    if (row.dob && !isValidDate(row.dob)) {
      errors.push(`Row ${rowIndex + 1}: Invalid date format (use YYYY-MM-DD)`);
    }
    
    // School validation with partial matching
    const schoolValidation = validateSchool(row.school, rowIndex);
    errors.push(...schoolValidation.errors);
    normalizedRow.school = schoolValidation.normalizedSchool;
    
    return { errors, normalizedRow };
  };

  const isValidDate = (dateString: string): boolean => {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateString)) return false;
    
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  };

  const parseFullName = (fullName: string): { first_name: string; last_name: string } => {
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) {
      return { first_name: parts[0], last_name: '' };
    }
    return {
      first_name: parts[0],
      last_name: parts.slice(1).join(' ')
    };
  };

  // School validation with partial matching
  const findMatchingSchool = (inputSchool: string): { match: string | null; suggestions: string[] } => {
    if (!inputSchool?.trim()) {
      return { match: null, suggestions: [] };
    }
    
    const allSchools = getAllSchools();
    const normalizedInput = inputSchool.toLowerCase().trim();
    
    // Exact match (case insensitive)
    const exactMatch = allSchools.find(school => 
      school.toLowerCase() === normalizedInput
    );
    if (exactMatch) {
      return { match: exactMatch, suggestions: [] };
    }
    
    // Score-based matching for better partial matches
    const scoredMatches = allSchools.map(school => {
      const normalizedSchool = school.toLowerCase();
      let score = 0;
      
      // High score: school contains the full input as a substring
      if (normalizedSchool.includes(normalizedInput)) {
        score += 100;
      }
      
      // Medium score: input words match school words
      const schoolWords = normalizedSchool.split(' ').filter(word => 
        word.length > 2 && !['school', 'elementary', 'middle', 'high'].includes(word)
      );
      const inputWords = normalizedInput.split(' ').filter(word => word.length > 2);
      
      // Count matching words
      const matchingWords = inputWords.filter(inputWord => 
        schoolWords.some(schoolWord => 
          schoolWord.includes(inputWord) || inputWord.includes(schoolWord)
        )
      );
      
      // High score if all input words match
      if (matchingWords.length === inputWords.length && inputWords.length > 0) {
        score += 50;
      }
      
      // Additional score for each matching word
      score += matchingWords.length * 10;
      
      // Bonus for consecutive word matches (e.g., "beach park" in "Beach Park Elementary")
      if (inputWords.length > 1) {
        const inputPhrase = inputWords.join(' ');
        if (normalizedSchool.includes(inputPhrase)) {
          score += 30;
        }
      }
      
      return { school, score };
    }).filter(item => item.score > 0);
    
    // Sort by score (highest first)
    scoredMatches.sort((a, b) => b.score - a.score);
    
    if (scoredMatches.length === 0) {
      return { match: null, suggestions: [] };
    }
    
    // If the top score is significantly higher than others, use it
    const topMatch = scoredMatches[0];
    const secondBestScore = scoredMatches.length > 1 ? scoredMatches[1].score : 0;
    
    // Auto-match if top score is much higher OR if it's a high-confidence match
    if (topMatch.score >= 100 || (topMatch.score >= 50 && topMatch.score > secondBestScore + 20)) {
      return { match: topMatch.school, suggestions: [] };
    }
    
    // Return suggestions for ambiguous cases
    return { 
      match: null, 
      suggestions: scoredMatches.slice(0, 3).map(item => item.school)
    };
  };

  const validateSchool = (schoolInput: string, rowIndex: number): { errors: string[]; normalizedSchool: string } => {
    if (!schoolInput?.trim()) {
      return { errors: [], normalizedSchool: '' }; // School is optional
    }
    
    const { match, suggestions } = findMatchingSchool(schoolInput);
    
    if (match) {
      return { errors: [], normalizedSchool: match };
    }
    
    if (suggestions.length > 0) {
      const suggestionText = suggestions.join(', ');
      return {
        errors: [`Row ${rowIndex + 1}: School "${schoolInput}" not found. Did you mean: ${suggestionText}?`],
        normalizedSchool: schoolInput
      };
    }
    
    return {
      errors: [`Row ${rowIndex + 1}: School "${schoolInput}" not recognized. Please check the school name.`],
      normalizedSchool: schoolInput
    };
  };

  const handleImport = async () => {
    if (!file) return;

    setIsUploading(true);
    setError(null);

    try {
      // Check authentication
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error('You must be logged in to import data');
      }
      
      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());
        const headers = lines[0].split(',').map(h => h.trim());
        
        const rows = lines.slice(1).map(line => {
          const values = line.split(',').map(v => v.trim());
          const row: Record<string, string> = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || '';
          });
          return row as unknown as CsvRow;
        });

        // Validate all rows and normalize school names
        const allErrors: string[] = [];
        const normalizedRows: CsvRow[] = [];
        rows.forEach((row, index) => {
          const validation = validateRow(row, index);
          allErrors.push(...validation.errors);
          normalizedRows.push(validation.normalizedRow);
        });

        if (allErrors.length > 0) {
          setError(`Validation errors:\n${allErrors.join('\n')}`);
          setIsUploading(false);
          return;
        }

        // Process imports
        const results: ImportResults = {
          success: 0,
          errors: [],
          newParents: 0,
          newStudents: 0
        };

        for (const row of normalizedRows) {
          try {
            const guardian1Name = parseFullName(row.guardian1_full_name);
            const guardian2Name = row.guardian2_full_name?.trim() ? parseFullName(row.guardian2_full_name) : null;
            const studentName = parseFullName(row.student_full_name);

            // Use the comprehensive CSV import function
            const { data: result, error: importError } = await supabase
              .rpc('csv_import_student_with_guardians', {
                // Required parameters first
                p_student_first_name: studentName.first_name,
                p_student_last_name: studentName.last_name,
                p_guardian1_first_name: guardian1Name.first_name,
                p_guardian1_last_name: guardian1Name.last_name,
                p_guardian1_email: normalizeEmail(row.guardian1_email || ''),
                // Optional parameters
                p_student_dob: row.dob || null,
                p_student_metadata: {
                  school: row.school || null,
                  grade: row.Grade || null
                },
                p_guardian1_phone: row.guardian1_phone_number || null,
                p_guardian2_first_name: guardian2Name?.first_name || null,
                p_guardian2_last_name: guardian2Name?.last_name || null,
                p_guardian2_email: row.guardian2_email ? normalizeEmail(row.guardian2_email) : null,
                p_guardian2_phone: row.guardian2_phone_number || null
              });

            if (importError) {
              throw new Error(importError.message);
            }

            if (result) {
              // Count newly created parents
              if (result.guardian1_created) results.newParents++;
              if (result.guardian2_created) results.newParents++;
              
              results.newStudents++;
              results.success++;
            }

          } catch (error) {
            console.error('Error processing row:', error);
            let errorMessage = 'Unknown error';
            
            if (error instanceof Error) {
              errorMessage = error.message;
            } else if (typeof error === 'object' && error !== null) {
              // Handle Supabase error objects
              const supabaseError = error as Record<string, unknown>;
              if (typeof supabaseError.message === 'string') {
                errorMessage = supabaseError.message;
              } else if (
                typeof supabaseError.error === 'object' &&
                supabaseError.error !== null &&
                typeof (supabaseError.error as Record<string, unknown>).message === 'string'
              ) {
                errorMessage = (supabaseError.error as Record<string, unknown>).message as string;
              } else {
                errorMessage = JSON.stringify(error);
              }
            }
            
            results.errors.push(`Error processing ${row.student_full_name}: ${errorMessage}`);
          }
        }

        setIsUploading(false);
        onSuccess(results);
      };

      reader.readAsText(file);
    } catch (error) {
      console.error('Import error:', error);
      const errorMessage = getErrorMessage(error);
      setError(`Import failed: ${errorMessage}`);
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreview([]);
    setShowPreview(false);
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Import Students from CSV">
      <div className="p-6">
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">CSV Import Instructions</h3>
          <div className="text-sm text-gray-600 mb-4">
            <p className="mb-2">Your CSV file must contain the following columns:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>guardian1_full_name</strong> - Guardian 1's full name (required)</li>
              <li><strong>guardian1_email</strong> - Guardian 1's email address (required)</li>
              <li><strong>guardian1_phone_number</strong> - Guardian 1's phone number (optional)</li>
              <li><strong>guardian2_full_name</strong> - Guardian 2's full name (optional)</li>
              <li><strong>guardian2_email</strong> - Guardian 2's email address (optional, but required if guardian2_full_name is provided)</li>
              <li><strong>guardian2_phone_number</strong> - Guardian 2's phone number (optional)</li>
              <li><strong>student_full_name</strong> - Student's full name (required)</li>
              <li><strong>Grade</strong> - Student's grade level (optional)</li>
              <li><strong>school</strong> - Student's school name (optional)</li>
              <li><strong>dob</strong> - Student's date of birth in YYYY-MM-DD format (optional)</li>
            </ul>
          </div>
        </div>

        <div className="mb-6">
          <label htmlFor="csvFile" className="block text-sm font-medium text-gray-700 mb-2">
            Select CSV File
          </label>
          <input
            type="file"
            id="csvFile"
            accept=".csv"
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            disabled={isUploading}
          />
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-md whitespace-pre-line">
            {error}
          </div>
        )}

        {showPreview && preview.length > 0 && (
          <div className="mb-6">
            <h4 className="text-md font-medium text-gray-900 mb-2">Preview (first 5 rows)</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Guardian 1</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Guardian 1 Email</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Guardian 1 Phone</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Guardian 2</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Guardian 2 Email</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Guardian 2 Phone</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">School</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {preview.map((row, index) => (
                    <tr key={index}>
                      <td className="px-3 py-2 text-sm text-gray-900">{row.guardian1_full_name}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{row.guardian1_email}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{row.guardian1_phone_number || '-'}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{row.guardian2_full_name || '-'}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{row.guardian2_email || '-'}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{row.guardian2_phone_number || '-'}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{row.student_full_name}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{row.Grade}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{row.school}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            disabled={isUploading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={!file || isUploading}
            className={`px-4 py-2 rounded-md text-white font-medium transition-colors ${
              !file || isUploading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isUploading ? 'Importing...' : 'Import CSV'}
          </button>
        </div>
      </div>
    </Modal>
  );
} 